import { Query } from "@tanstack/react-query";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { Spin } from "antd";
import { isWorkspaceCurrencyReconverting, useGetWorkspaceCurrencyQuery } from "api/TenantSlug/Setting/hooks";
import { ReportsLockedResult } from "components/UI/ReportsLockedResult/ReportsLockedResult";

import GetWorkspaceCurrencyResponse = App.Http.Responses.Setting.GetWorkspaceCurrencyResponse;

export const Route = createFileRoute("/_authenticated/$tenantSlug/_layout/report/_layout")({
    component: ReportLayout,
    loader: ({ context: { t } }) => {
        return {
            breadcrumb: t("pages.report.title"),
        };
    },
});

export function ReportLayout() {
    const workspaceCurrencyQuery = useGetWorkspaceCurrencyQuery({
        refetchInterval: (query: Query<GetWorkspaceCurrencyResponse>) =>
            isWorkspaceCurrencyReconverting(query.state.data) ? 5000 : false,
    });
    const reconverting = isWorkspaceCurrencyReconverting(workspaceCurrencyQuery.data);

    // Wait for the first resolution before rendering children at all — on mount `data` is
    // undefined so `reconverting` would otherwise read as false for a moment, letting report
    // pages mount and fire their own queries before we know a reconversion is running. Those
    // then hit the BE's 423 lock independently and each show their own error state/toast
    // instead of this shared banner.
    if (workspaceCurrencyQuery.isLoading || workspaceCurrencyQuery.isError || !workspaceCurrencyQuery.data) {
        return (
            <div className="flex justify-center items-center h-64">
                <Spin size="large" />
            </div>
        );
    }

    // Poll while a reconversion is running so the banner clears itself once the background job
    // finishes — mirrors the BE's 423 lock on the report endpoints (see
    // EnsureWorkspaceReportsNotLocked middleware), but proactively instead of waiting for a
    // failed report request.
    if (reconverting) {
        const fromCurrency = workspaceCurrencyQuery.data.reconversion_from_currency;
        const toCurrency = workspaceCurrencyQuery.data.reconversion_to_currency;

        if (!fromCurrency || !toCurrency) {
            return (
                <div className="flex justify-center items-center h-64">
                    <Spin size="large" />
                </div>
            );
        }

        return (
            <ReportsLockedResult
                fromCurrency={fromCurrency}
                toCurrency={toCurrency}
                progressPercent={workspaceCurrencyQuery.data?.reconversion_progress_percent}
            />
        );
    }

    return <Outlet />;
}
